<template>
  <v-row dense>
    <v-col cols="12" md="6">
      <v-row dense>
        <v-col cols="12" md="6">
          <span v-html="output.price"></span>
        </v-col>
        <v-col cols="12" md="6">
          <strong>{{ output.shelf_time >= 60 ? '>60' : output.shelf_time }}</strong> days on shelf
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12" md="6">
          <strong>{{ output.sold_out_time }}%</strong> time sold-out
        </v-col>
        <v-col cols="12" md="6">
          <v-tooltip bottom v-if="output.shelf_time <= 14">
            <template v-slot:activator="{ on }">
                <span v-on="on">
                  <v-icon small  color="red"
                  >mdi-alert</v-icon>
                </span>
            </template>
            <span>Error could be very large when on-shelf period is short</span>
          </v-tooltip>
          <strong>{{ output.projected_sales_per_cycle }}</strong> proj. coils
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12" md="6">
          <span v-if="!product.location_product.expired_at">No expiry date</span>
          <span v-else-if="expiredIn() > 0">
            <strong>{{expiredIn()}}</strong>-day to expire
          </span>
          <strong class="red--text" v-else>Expired</strong>
        </v-col>
      </v-row>
    </v-col>
    <v-col cols="12" md="6">
      <v-row dense>
        <v-col cols="12">
          <b>{{ output.coil_number || '/' }}</b>-slot coil,
          <template v-if="isDrinkMachine(locationData.machine_model)">x
            <b
              v-for="(size, i) in output.drink_coil_acceptable_sizes"
              :key="i"
              :class="{
                'red--text': size === 'full',
                'blue--text': size === 'half',
                'green--text': size === 'quarter'
              }">&nbsp;{{size === 'full' ? '全' : (size === 'half' ? '半' : '小')}}</b>
          </template>
          <b v-if="!isDrinkMachine(locationData.machine_model)"
            :class="{
            'red--text': output.height === 'high',
            'blue--text': output.height === 'low'
          }">{{output.height === 'high' ? '高' : '低'}}</b>
          product
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12">
          <span>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <span v-on="on">
                  <v-icon small
                  >touch_app</v-icon>
                  {{ locationData.engagement }}
                </span>
              </template>
              <span>{{ locationData.engagement }} people touched the machine in past 30 days</span>
            </v-tooltip>
            →
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <span v-on="on">
                  <v-icon small
                  >mdi-cube-scan</v-icon>
                  {{ product.location_product ? product.location_product.engagement : 0 }}
                  (<strong>{{locationData.engagement ? ((product.location_product ? product.location_product.engagement : 0) / locationData.engagement * 100).toFixed(0) : 0}}%</strong>)
                </span>
              </template>
              <span>{{ product.location_product ? product.location_product.engagement : 0 }} people viewed this product in past 30 days</span>
            </v-tooltip>
             →
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <span v-on="on">
                  <v-icon small
                  >shopping_cart</v-icon>
                  {{product.location_product ? product.location_product.purchase : 0}}
                  (<strong>{{ product.location_product && product.location_product.engagement ? (product.location_product.purchase / product.location_product.engagement * 100).toFixed(0) : 0}}%</strong>)
                </span>
              </template>
              <span>{{product.location_product ? product.location_product.purchase : 0}} people bought this product in past 30 days</span>
            </v-tooltip>
          </span>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12" v-if="hasCapacityChanges">
          <span class="grey--text caption" v-if="capacityChanges.length">Most recent 3 capacity changes:</span>
          <span class="caption" v-for="(change, i) in capacityChanges" :key="i">
            <br/>Changed to
            <strong>{{ (change.capacity / product.coil_number).toFixed(2) }}</strong> coils on
            <strong>{{ timestampToDate(change.time) }}</strong>
          </span>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>

<script>
  import productMixin from '@/mixins/product'
  import dateMixin from '@/mixins/date'
  import coilSettingMixin from '@/mixins/coilSetting'

  export default {
    props: {
      product: Object,
      locationData: Object
    },

    mixins: [
      productMixin,
      dateMixin,
      coilSettingMixin
    ],

    data () {
      return {
        output: {},

        dataPointInterval: 6,
        editingCoilIndex: null,
        editProductDialog: false,
        editingCoil: null
      }
    },

    computed: {
      contractType: function () {
        switch (this.product.contract_type_id) {
          case null:
          case '0e1adc11-6bef-4884-a2a7-9c2639778c63':
            return 'Gritus'
          case '3077e5c9-a380-484c-9ae2-a9511afeeacd':
            return 'Slotting'
          case '42c47e34-9abd-475d-b24d-c4b167cf8c8b':
            return 'Consignment'
          case 'b3bc62d1-6e8e-46eb-b55a-24dad9672c6f':
            return 'Owner'
        }
      },

      capacityChanges () {
        if (!this.product.analytics_data) {
          return []
        }
        let result = []
        for (let i = this.product.analytics_data.capacity_changes.length; i >= 1; i--) {
          result.push(this.product.analytics_data.capacity_changes[i - 1])
          if (result.length >= 3) {
            break
          }
        }
        return result
      },

      hasCapacityChanges () {
        return this.product.analytics_data && this.product.analytics_data.capacity_changes !== null
      }
    },

    created () {
      this.output = this.getOutputData(this.product, this.locationData)
    },

    methods: {
      expiredIn () {
        return this.daysToGo(this.product.location_product.expired_at)
      }
    }
  }
</script>



// WEBPACK FOOTER //
// src/components/Common/Rental/Locations/Products/LocationProduct/Detail.vue